import React, { useState, useEffect } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import { fetchUsers } from "../../services/user.service";
import { activateUser } from "../../services/user.service";
import { useAuth } from "context/AuthContext";
import UserToolbar from "./UserToolbar";
import { getFullAvatarUrl, getInitials } from "utils/imageUtils";
import {
  Avatar,
  IconButton,
  Tooltip,
  Box,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import KeyIcon from "@mui/icons-material/Key";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonIcon from "@mui/icons-material/Person";
import { formatDate } from "utils/formatters";
import "./UserList.css";
import "./UserTable.css"; // File CSS mới để xử lý vấn đề thanh cuộn
import { AppConfirmDialog } from "components/common/dialogs";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import ChangePasswordModal from "./ChangePasswordModal";
import DataTable from "components/common/tables/DataTable";

const EmptyState = ({ searchTerm, statusFilter }) => {
  let message = "Không có người dùng nào để hiển thị";

  if (searchTerm) {
    message = `Không tìm thấy người dùng nào khớp với từ khóa "${searchTerm}"`;
  } else if (statusFilter && statusFilter !== "Tất cả") {
    message = `Không có người dùng nào có trạng thái "${statusFilter}"`;
  }
  return (
    <Box className="empty-state">
      <span className="material-icons">search</span>
      <Typography variant="h6">{message}</Typography>
      <Typography variant="body2">
        Thử thay đổi bộ lọc hoặc tạo người dùng mới
      </Typography>
    </Box>
  );
};

const normalizeUser = (item) => ({
  ...item,
  userId: item.userId ?? item.UserId,
  userName: item.userName ?? item.UserName,
  fullName: item.fullName ?? item.FullName,
  email: item.email ?? item.Email,
  isActive: item.isActive ?? item.IsActive,
  personnelId: item.personnelId ?? item.PersonnelId,
  personnelName: item.personnelName ?? item.PersonnelName ?? item.personnelFullName ?? item.PersonnelFullName,
  roleName: item.roleName ?? item.RoleName,
  avatarFilePath: item.avatarFilePath ?? item.AvatarFilePath,
  createdAt: item.createdAt ?? item.CreatedAt,
  updatedAt: item.updatedAt ?? item.UpdatedAt,
});

const UserList = () => {
  // Search term state
  const [searchTerm, setSearchTerm] = useState("");
  const {
    user,
  } = useAuth();

  const [users, setUsers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState("userId");
  const [sortDirection, setSortDirection] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("Tất cả");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToActivate, setUserToActivate] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("warning");

  // Initial load and on filters/pagination change
  useEffect(() => {
    // Chỉ load danh sách người dùng khi có user
    if (user) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortColumn, sortDirection, statusFilter, user]);
  // Chỉ hiện loading khi đang tải dữ liệu
  if (!user) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography>Đang tải thông tin người dùng...</Typography>
      </Box>
    );
  }

  // ProtectedRoute đã đảm bảo rằng chỉ Admin mới thấy được component này
  const loadUsers = async (
    currentStatusFilter = statusFilter,
    currentSearchTerm = searchTerm
  ) => {
    try {
      setLoading(true);
      setError(null); // Convert status filter to boolean for API
      let isActive = null;

      if (currentStatusFilter === "Đã kích hoạt") {
        isActive = true;
        // console.log("Setting isActive to TRUE");
      } else if (currentStatusFilter === "Chưa kích hoạt") {
        isActive = false;
      } else {

      }

      const response = await fetchUsers(
        pageSize,
        currentPage,
        currentSearchTerm, // Use the provided search term parameter
        sortColumn,
        sortDirection.toUpperCase(),
        isActive
      ); // Log the response to debug

      // Ensure we have valid data before setting state
      if (response && Array.isArray(response.items)) {
        // New structure returned from updated user.service.js
        setUsers(response.items.map(normalizeUser));
        setTotalItems(response.totalItems || 0);
      } else if (
        response &&
        response.data &&
        Array.isArray(response.data.items)
      ) {
        // Old structure (should not happen anymore, but keeping just in case)
        setUsers(response.data.items.map(normalizeUser));
        setTotalItems(response.data.totalItems || 0);
      } else if (response && Array.isArray(response)) {
        // Directly returned array
        setUsers(response.map(normalizeUser));
        setTotalItems(response.length);
      } else {
        // Set default values if response data structure is unexpected
        console.error("Unexpected API response structure:", response);
        setUsers([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError("Không thể tải danh sách người dùng. Vui lòng thử lại sau.");
      // Reset user data on error
      setUsers([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    // Update the state
    setStatusFilter(status);
    setCurrentPage(1);
    loadUsers(status, searchTerm);
  };
  const handleTablePaginationChange = (updater) => {
    const pagination = { pageIndex: currentPage - 1, pageSize };
    const nextPagination =
      typeof updater === "function" ? updater(pagination) : updater;

    setPageSize(nextPagination.pageSize);
    setCurrentPage(
      nextPagination.pageSize !== pageSize ? 1 : nextPagination.pageIndex + 1
    );
  };

  const handleTableSortingChange = (updater) => {
    const sorting = [{ id: sortColumn, desc: sortDirection === "desc" }];
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    const nextSort = nextSorting[0];

    if (nextSort) {
      setSortColumn(nextSort.id);
      setSortDirection(nextSort.desc ? "desc" : "asc");
      setCurrentPage(1);
    }
  };

  // Kiểm tra quyền update
  const handleEditUser = async (user) => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.USER_UPDATE);
    if (result.hasPermission) {
      setSelectedUser(user);
      setEditUserOpen(true);
    } else {
      setSnackbarMessage("Bạn không có quyền cập nhật người dùng.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };
  // Add user handler
  const handleAddUser = async () => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.USER_CREATE);
    if (result.hasPermission) {
      setAddUserOpen(true);
    } else {
      setSnackbarMessage("Bạn không có quyền thêm người dùng.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };

  // Toggle active handler
  const handleToggleActive = async (user) => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.USER_UPDATE);
    if (result.hasPermission) {
      if (!user.isActive && !user.personnelId) {
        setSnackbarMessage("Chưa gán nhân sự nên không kích hoạt được tài khoản.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      setUserToActivate(user);
      setConfirmDialogOpen(true);
    } else {
      setSnackbarMessage(
        "Bạn không có quyền kích hoạt/hủy kích hoạt tài khoản."
      );
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };

  // Change password handler
  const handleChangePassword = async (user) => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.USER_UPDATE);
    if (result.hasPermission) {
      setSelectedUser(user);
      setChangePasswordOpen(true);
    } else {
      setSnackbarMessage("Bạn không có quyền đổi mật khẩu người dùng.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };

  // Confirm toggle active
  const confirmToggleActive = async () => {
    if (!userToActivate) return;
    if (!userToActivate.isActive && !userToActivate.personnelId) {
      setConfirmDialogOpen(false);
      setUserToActivate(null);
      setSnackbarMessage("Chưa gán nhân sự nên không kích hoạt được tài khoản.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    try {
      setUpdatingStatus(true);
      setLoading(true);
      await activateUser(userToActivate.userId, !userToActivate.isActive);
      setConfirmDialogOpen(false);
      setUserToActivate(null);
      loadUsers();
    } catch (err) {
      setError("Không thể cập nhật trạng thái người dùng.");
    } finally {
      setUpdatingStatus(false);
      setLoading(false);
    }
  };
  // Search handler
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    loadUsers(statusFilter, term);
  };

  // ...existing code...

  const handleUserAdded = () => {
    loadUsers();
  };

  const handleUserUpdated = () => {
    loadUsers();
  };
  const UserStatusBadge = ({ isActive, personnelId }) => {
    if (!personnelId) {
      return (
        <span
          className="status-badge"
          style={{
            backgroundColor: "#9e9e9e",
          }}
        >
          Chưa liên kết
        </span>
      );
    }

    return (
      <span
        className="status-badge"
        style={{
          backgroundColor: isActive
            ? "var(--status-completed, #4caf50)"
            : "var(--status-pending, #ff9800)",
        }}
      >
        {isActive ? "Đã kích hoạt" : "Chưa kích hoạt"}
      </span>
    );
  };

  const RoleBadge = ({ roleName }) => {
    let color = "#9e9e9e"; // Default color

    // Màu sắc tương ứng với từng vai trò
    if (roleName === "Admin") {
      color = "#9c27b0"; // Purple for Admin
    } else if (roleName === "Operator") {
      color = "#2196f3"; // Blue for Operator
    } else if (roleName === "Creator") {
      color = "#4caf50"; // Green for Creator
    } else if (roleName === "Viewer") {
      color = "#798e01ff"; // Cyan for Viewer
    }

    return (
      <span
        className="role-badge"
        style={{
          backgroundColor: color,
        }}
      >
        {roleName || "Không có"}{" "}
      </span>
    );
  };

  const columns = [
    {
      accessorKey: "userId", header: "ID", size: 40,
    },
    {
      id: "avatar", header: "Avatar", size: 60, enableSorting: false,
      muiTableBodyCellProps: () => ({ align: "center" }),
      Cell: ({ row }) => (
        <Avatar
          src={getFullAvatarUrl(row.original.avatarFilePath)}
          alt={row.original.fullName || row.original.userName}
          className="user-avatar"
          variant="rounded"
          sx={{ margin: "0 auto" }}
          imgProps={{
            onError: (e) => {
              e.target.onerror = null;
              e.target.style.display = "none";
            },
            referrerPolicy: "origin",
            crossOrigin: "anonymous",
          }}
        >
          {getInitials(row.original.fullName || row.original.userName)}
        </Avatar>
      ),
    },
    {
      accessorKey: "userName", header: "Tên đăng nhập", size: 100,
    },
    {
      accessorKey: "fullName", header: "Tên người dùng", size: 140,
      Cell: ({ cell }) => cell.getValue() || "Chưa cập nhật",
    },
    {
      accessorKey: "email", header: "Email", size: 140,
      Cell: ({ cell }) => cell.getValue() || "Chưa cập nhật",
    },
    {
      accessorKey: "roleName", header: "Vai trò", size: 80,
      Cell: ({ cell }) => <RoleBadge roleName={cell.getValue()} />,
    },
    {
      accessorKey: "isActive", header: "Trạng thái", size: 100,
      Cell: ({ row }) => (
        <UserStatusBadge
          isActive={row.original.isActive}
          personnelId={row.original.personnelId}
        />
      ),
    },

    {
      accessorKey: "createdAt", header: "Ngày tạo", size: 80,
      Cell: ({ cell }) => (
        formatDate(cell.getValue()) || ""
      ),
    },
    {
      accessorKey: "updatedAt", header: "Ngày cập nhật", size: 100,
      Cell: ({ cell }) => (
        cell.getValue() ? formatDate(cell.getValue()) : "Chưa cập nhật"
      ),
    },
    {
      id: "actions",
      header: "Thao tác",
      size: 90,
      enableSorting: false,
      enableColumnHiding: false,
      Cell: ({ row }) => (
        <Box>
          <Tooltip title={
            !row.original.personnelId
              ? "Chưa liên kết nhân sự"
              : row.original.isActive ? "Hủy kích hoạt" : "Kích hoạt"
          }>
            <span>
              <IconButton
                size="small"
                color={row.original.isActive ? "warning" : "success"}
                onClick={() => handleToggleActive(row.original)}
                disabled={!row.original.isActive && !row.original.personnelId}
              >
                {row.original.isActive ? (
                  <PersonOffIcon fontSize="small" />
                ) : (
                  <PersonIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEditUser(row.original)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Đổi mật khẩu">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleChangePassword(row.original)}
            >
              <KeyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <div className="users-container">
      <AppSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
        message={snackbarMessage}
      />
      <div className="fixed-header">
        <UserToolbar
          onSearch={handleSearch}
          onStatusChange={handleStatusChange}
          statusFilter={statusFilter}
          initialSearchTerm={searchTerm}
          onAddUser={handleAddUser}
        />
      </div>
      <div className="scrollable-content">
        {error && <div className="error-message">{error}</div>}
        {users.length === 0 ? (
          <EmptyState searchTerm={searchTerm} statusFilter={statusFilter} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={users}
              enableSorting
              enableSortingRemoval={false}
              manualSorting
              manualPagination
              rowCount={totalItems}
              rowsPerPageOptions={[5, 10, 25, 50]}
              enableGlobalFilter={false}
              getRowId={(row) => String(row.userId)}
              onPaginationChange={handleTablePaginationChange}
              onSortingChange={handleTableSortingChange}
              state={{
                isLoading: loading,
                pagination: { pageIndex: currentPage - 1, pageSize },
                sorting: [{ id: sortColumn, desc: sortDirection === "desc" }],
              }}
            />
          </>
        )}
        {/* Modals */}
        <AddUserModal
          open={addUserOpen}
          onClose={() => setAddUserOpen(false)}
          onUserAdded={handleUserAdded}
        />
        {selectedUser && (
          <EditUserModal
            open={editUserOpen}
            onClose={() => setEditUserOpen(false)}
            user={selectedUser}
            onUserUpdated={handleUserUpdated}
          />
        )}
        {selectedUser && (
          <ChangePasswordModal
            open={changePasswordOpen}
            onClose={() => setChangePasswordOpen(false)}
            user={selectedUser}
          />
        )}{" "}
        {/* Confirmation Dialog */}
        <AppConfirmDialog
          open={confirmDialogOpen}
          title={userToActivate?.isActive ? "Hủy kích hoạt người dùng" : "Kích hoạt người dùng"}
          message={userToActivate?.isActive
            ? `Bạn có chắc chắn muốn hủy kích hoạt tài khoản ${userToActivate?.userName || ""}?`
            : `Bạn có chắc chắn muốn kích hoạt tài khoản ${userToActivate?.userName || ""}?`}
          type={userToActivate?.isActive ? "warning" : "confirm"}
          confirmText={userToActivate?.isActive ? "Hủy kích hoạt" : "Kích hoạt"}
          loading={updatingStatus}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={confirmToggleActive}
        />
      </div>
    </div>
  );
};

export default UserList;
